import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { IconButton } from '../IconButton/IconButton';
import useApplicationUid from '../../hooks/useApplicationUid';
import { selectBankAccounts } from '../../store/selectors/bankAccountSelectors';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  APPLICATION_FUNDING_TYPE,
  REGISTRATION_FLOW_TYPES,
} from '../../util/constants';
import { fetchBankAccounts } from '../../store/actions/bankAccount/bankAccountActions';
import Paragraph from '../Paragraph/Paragraph';
import EditIcon from '../Icons/Edit';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import themeColors from '../../assets/styles/themeColors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { REGISTRATION_FLOW_TYPE } from '../../constants/sessionStorage';

const FundingReviewContainer = styled.div`
  padding: 1.5rem;
  background-color: ${themeColors.colorBackgroundSecondary};
  margin-bottom: ${(props) => (props.mb ? `${props.mb}px` : 0)};
  margin-top: ${(props) => (props.mt ? `${props.mt}px` : 0)};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 1rem;
`;

const FundingReviewContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 4px;
`;

const EditIconWrap = styled(IconButton)`
  color: ${themeColors.colorTextSecondary};
`;

const FundingReview = ({ funding, mb, mt, onEdit, missingInformation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [bankAccount, setBankAccount] = useState({});

  const { ApplicationUid } = useApplicationUid();
  const bankAccounts = useSelector(selectBankAccounts);
  const registrationFlowType = retrieveFromSessionStorage(
    REGISTRATION_FLOW_TYPE,
  );
  const isAIPFlow = registrationFlowType === REGISTRATION_FLOW_TYPES.AIP;

  const applicationFundingType = parseEnumType(
    APPLICATION_FUNDING_TYPE,
    funding?.BankFundingType,
  );
  const isPlaid =
    applicationFundingType === parseEnumType(APPLICATION_FUNDING_TYPE, 1);

  const description = {
    ACHConnected: t('standardReview.fundTypes.plaid', {
      bankName: bankAccount?.BankName,
      last4digits: bankAccount?.AccountNumber?.substr(
        (bankAccount?.AccountNumber).length - 4,
      ),
    }),
    ACHManual: t('standardReview.fundTypes.manual'),
    Wire: t('standardReview.fundTypes.wire'),
    Check: t('standardReview.fundTypes.check'),
    Cryptocurrency: t('standardReview.fundTypes.crypto'),
  };

  useEffect(() => {
    if (funding && isPlaid) {
      const { BankAccountUid } = funding;

      if (BankAccountUid && !isEmpty(bankAccounts)) {
        const selectedBankAccount = bankAccounts.find(
          (bank) => bank.BankAccountUid === BankAccountUid,
        );
        if (selectedBankAccount) {
          setBankAccount(selectedBankAccount);
        }
      }
    }
  }, [funding, funding?.BankAccountUid, bankAccounts, isPlaid]);

  useEffect(() => {
    if (isPlaid) {
      dispatch(
        fetchBankAccounts({
          ApplicationUid,
        }),
      );
    }
  }, [ApplicationUid, dispatch, isPlaid]);
  if (isEmpty(funding)) {
    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
            No funding instructions added.
          </Paragraph>
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  if (isAIPFlow) {
    return <></>;
  }

  if (isEmpty(funding)) {
    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
            No funding instructions added.
          </Paragraph>
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  if (missingInformation) {
    return (
      <FundingReviewContainer mb={mb} mt={mt}>
        <FundingReviewContent>
          <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
            Application funding type is defined, but amount is missing.
          </Paragraph>
        </FundingReviewContent>
        <EditIconWrap onClick={onEdit}>
          <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
        </EditIconWrap>
      </FundingReviewContainer>
    );
  }

  return (
    <FundingReviewContainer mb={mb} mt={mt}>
      <FundingReviewContent>
        <Paragraph fontWeight={700}>
          {formatMoneyNumeral(funding?.Amount)} {t('common.deposit')}
        </Paragraph>
        <Paragraph variant="normal" color={themeColors.colorTextXLLight}>
          {
            description[
              parseEnumType(APPLICATION_FUNDING_TYPE, funding?.BankFundingType)
            ]
          }
        </Paragraph>
      </FundingReviewContent>
      <EditIconWrap onClick={onEdit}>
        <EditIcon w="24px" h="24px" color={themeColors.colorTextSecondary} />
      </EditIconWrap>
    </FundingReviewContainer>
  );
};

FundingReview.propTypes = {
  mb: PropTypes.number,
  mt: PropTypes.number,
  onEdit: PropTypes.func,
  funding: PropTypes.shape({
    BankAccountUid: PropTypes.string,
    BankFundingType: PropTypes.number,
    Amount: PropTypes.number,
  }),
  missingInformation: PropTypes.bool,
};

export default FundingReview;
